// Beskeder › Send besked til én eller flere kunder – samme kode i testversionen og i drift:
//  • Modtagere: slå kunden op (navn, mobil, mail, nummerplade), alle med booking en bestemt dag – eller én steamers kunder den
//    dag (fx når steameren er syg). Hver modtager har evt. en booking, som flettefelterne udfyldes fra.
//  • Formål: service (sendes til alle) eller markedsføring (kun kunder med samtykke, der ikke har afmeldt sig – og med
//    afmeldingslinket i bunden). Kunder uden mobil/mail til kanalen springes over og vises.
//  • Spærret udsendelse (klienter.udsendelse_spaerret): intet sendes og intet lægges i send-køen – det vises tydeligt.
import { udfyld } from './automation.ts';
import { afmeldLink, medAfmelding } from './samtykke.ts';
import { rens, rensTekst } from './validering.ts';
import { ApiFejl } from './fejl.ts';

export type Modtager = {
  kunde_id: string; navn: string; email: string; telefon: string; booking_id: number | null;
  samtykke: boolean; afmeldt: boolean; afmeld_token?: string | null;
  /** Kort beskrivelse til listen, fx "10:00–12:00 · Jamil · Vesterbrogade 12". */
  info?: string; steamer_id?: string | null;
};
export type FlereKanal = 'sms' | 'mail' | 'begge';
export type FlereInput = {
  modtagere: { kunde_id: string; booking_id: number | null }[];
  kanal: FlereKanal; formaal: 'service' | 'markedsfoering';
  emne: string; tekst: string; smsTekst: string;
  skabelon?: string;      // skabelonens navn i loggen (tom = "Direkte besked")
};
export type FlereSvar = { sendt: number; modtagere: number; sprunget: { navn: string; grund: string }[] };
/** Én besked klar til køen. */
export type FlereUdkast = { kunde_id: string; booking_id: number | null; kanal: 'sms' | 'mail'; emne: string; tekst: string };

export const MAKS_MODTAGERE = 200;
export const SPAERRET_LANCERING = 'Udsendelse er spærret indtil lancering – intet bliver sendt.';

/** Hvem kan få beskeden, og hvem springes over (og hvorfor)? */
export function udvaelg(l: Modtager[], kanal: FlereKanal, formaal: FlereInput['formaal']): { send: Modtager[]; sprunget: FlereSvar['sprunget'] } {
  const send: Modtager[] = []; const sprunget: FlereSvar['sprunget'] = [];
  for (const m of l) {
    const harMail = !!m.email.trim(), harSms = !!m.telefon.trim();
    if (formaal === 'markedsfoering' && (m.afmeldt || !m.samtykke)) sprunget.push({ navn: m.navn, grund: m.afmeldt ? 'Har afmeldt sig markedsføring' : 'Har ikke sagt ja til markedsføring' });
    else if (kanal === 'sms' && !harSms) sprunget.push({ navn: m.navn, grund: 'Intet mobilnummer' });
    else if (kanal === 'mail' && !harMail) sprunget.push({ navn: m.navn, grund: 'Ingen e-mail' });
    else if (kanal === 'begge' && !harSms && !harMail) sprunget.push({ navn: m.navn, grund: 'Hverken mobil eller e-mail' });
    else send.push(m);
  }
  return { send, sprunget };
}

/** Tjek teksterne, før noget flettes eller sendes. */
export function tjekFlere(a: FlereInput): void {
  if (!a.modtagere.length) throw new ApiFejl('Vælg mindst én modtager.');
  if (a.modtagere.length > MAKS_MODTAGERE) throw new ApiFejl(`Højst ${MAKS_MODTAGERE} modtagere ad gangen.`);
  if (a.kanal !== 'sms' && rensTekst(a.tekst, 5000).length < 2) throw new ApiFejl('Skriv mailen.', { mail: 'Skriv mailen.' });
  if (a.kanal !== 'sms' && rens(a.emne, 200).length < 2) throw new ApiFejl('Skriv et emne.', { emne: 'Skriv et emne.' });
  if (a.kanal !== 'mail' && rensTekst(a.smsTekst, 1000).length < 2) throw new ApiFejl('Skriv SMS’en.', { sms: 'Skriv SMS’en.' });
}

/** Beskederne til én modtager, flettet med hans felter (v) – markedsføring med afmeldingslinket (base = virksomhedens adresse). */
export function bygUdkast(m: Modtager, a: FlereInput, v: Record<string, string>, base: string, firma: string): FlereUdkast[] {
  const ud: FlereUdkast[] = [];
  const link = a.formaal !== 'markedsfoering' ? '' : v.link_afmeld || (m.afmeld_token ? afmeldLink(base, m.afmeld_token) : '');
  const af = (t: string, k: 'mail' | 'sms') => (link ? medAfmelding(t, k, link, firma) : t);
  if (a.kanal !== 'sms' && m.email.trim()) ud.push({ kunde_id: m.kunde_id, booking_id: m.booking_id, kanal: 'mail', emne: udfyld(rens(a.emne, 200), v), tekst: af(udfyld(rensTekst(a.tekst, 5000), v), 'mail') });
  if (a.kanal !== 'mail' && m.telefon.trim()) ud.push({ kunde_id: m.kunde_id, booking_id: m.booking_id, kanal: 'sms', emne: '', tekst: af(udfyld(rensTekst(a.smsTekst, 1000), v), 'sms') });
  return ud;
}

/** Kunde uden booking: kun navn, firma og telefon kan flettes. */
export const kundeFelter = (navn: string, firma: string, telefon: string): Record<string, string> =>
  ({ kunde_navn: navn, kunde_fornavn: navn.trim().split(/\s+/)[0] ?? navn, firma, telefon });
